import React from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';
import { Lock, Sparkles, Mail } from 'lucide-react';

/**
 * BUG 05/15 — Locked-suite placeholder shown when a client navigates directly to
 * a suite URL their company has NOT contracted. Bilingual via i18n keys.
 * "Request suite" CTA opens a pre-filled mailto: until Resend DNS swap lands.
 */
export default function SuiteLocked({ name, code }) {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const isES = (i18n.language || '').startsWith('es');

  const onRequest = () => {
    const to = 'info@corporatetalenthub.com';
    const subject = isES
      ? `Solicitud de activación: ${name}`
      : `Suite activation request: ${name}`;
    const lines = isES
      ? [
          `Hola equipo de Corporate Talent Hub,`,
          ``,
          `Quisiéramos activar la suite "${name}" para nuestra organización.`,
          ``,
          `• Solicitante: ${user?.name || ''} (${user?.email || ''})`,
          `• Suite solicitada: ${name} (${code})`,
          ``,
          `Quedamos atentos para coordinar próximos pasos.`,
          ``,
          `Gracias.`,
        ]
      : [
          `Hello Corporate Talent Hub team,`,
          ``,
          `We would like to activate the "${name}" suite for our organisation.`,
          ``,
          `• Requested by: ${user?.name || ''} (${user?.email || ''})`,
          `• Requested suite: ${name} (${code})`,
          ``,
          `Please let us know the next steps.`,
          ``,
          `Thank you.`,
        ];
    const body = encodeURIComponent(lines.join('\n'));
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${body}`;
  };

  return (
    <>
      <Topbar
        title={name}
        subtitle={t('suite.locked.subtitle', { defaultValue: 'Suite not included in your plan' })}
      />
      <div className="p-6 lg:p-8" data-testid={`suite-locked-${code}`}>
        <Card className="p-8 md:p-10 shadow-card relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-2xl">
            <div className="inline-flex items-center gap-2 mb-4">
              <Badge variant="secondary" className="text-xs uppercase tracking-wider gap-1.5">
                <Lock className="h-3 w-3" />
                {t('suite.locked.badge', { defaultValue: 'Locked' })}
              </Badge>
              <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/30 text-xs gap-1.5">
                <Sparkles className="h-3 w-3" />
                {code}
              </Badge>
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-3">
              {name}
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed mb-6">
              {t('suite.locked.body', {
                defaultValue: 'This suite is not included in your current plan. Contact your Corporate Talent Hub consultant to enable {{name}} for your organisation.',
                name,
              })}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={onRequest}
                data-testid={`suite-locked-${code}-request`}
                className="gap-2 bg-gradient-primary hover:opacity-90"
              >
                <Mail className="h-4 w-4" />
                {t('suite.locked.requestCta', { defaultValue: 'Request {{name}}', name })}
              </Button>
              <span className="text-xs text-muted-foreground">
                {t('suite.locked.requestHint', { defaultValue: 'Opens a pre-filled email to your Corporate Talent Hub consultant.' })}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}
